import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Rocket, Users, GraduationCap, Video, Calendar, CheckCircle2,
  ChevronRight, ChevronLeft, X, Sparkles, Heart, ArrowRight
} from "lucide-react";

const WIZARD_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Goodwin Cares!",
    subtitle: "Let's get you set up for success",
    description: "This quick tour will help you get started with the essential features you'll use every day to provide compassionate care.",
    icon: Rocket,
    color: "bg-gradient-to-br from-primary to-indigo-600",
    action: null,
    tips: [
      "Takes about 3 minutes to complete",
      "You can skip and return anytime",
      "Earn a 'Quick Starter' badge!"
    ]
  },
  {
    id: "patients",
    title: "Your Patient Caseload",
    subtitle: "Meet the families you'll be caring for",
    description: "View your assigned patients, their care plans, and health status. You can communicate with families and track their journey here.",
    icon: Users,
    color: "bg-gradient-to-br from-blue-500 to-cyan-500",
    action: { label: "View My Patients", path: "/staff/patients" },
    tips: [
      "Check patient details and care plans",
      "Send messages to families",
      "Log visit notes and observations"
    ]
  },
  {
    id: "training",
    title: "Hospice Academy Training",
    subtitle: "ELNEC-aligned courses with CEU credits",
    description: "Complete required training courses to earn CEU credits. Start with the ELNEC Core Curriculum for foundational hospice care knowledge.",
    icon: GraduationCap,
    color: "bg-gradient-to-br from-purple-500 to-pink-500",
    action: { label: "Start Training", path: "/academy" },
    tips: [
      "56+ courses across 11 specialty tracks",
      "Earn downloadable CEU certificates",
      "Track your progress and achievements"
    ]
  },
  {
    id: "telehealth",
    title: "Telehealth Video Calls",
    subtitle: "Connect face-to-face from anywhere",
    description: "Use HIPAA-compliant video calls to check in with patients and families. Great for quick consultations and emotional support.",
    icon: Video,
    color: "bg-gradient-to-br from-green-500 to-emerald-500",
    action: { label: "Try Telehealth", path: "/telehealth" },
    tips: [
      "Test your camera and microphone",
      "Record calls for documentation",
      "Screen share for education"
    ]
  },
  {
    id: "scheduling",
    title: "Smart Scheduling",
    subtitle: "AI-optimized visit planning",
    description: "Schedule patient visits with intelligent routing suggestions. The system helps optimize your day for efficiency and better care.",
    icon: Calendar,
    color: "bg-gradient-to-br from-orange-500 to-amber-500",
    action: { label: "View Schedule", path: "/scheduling" },
    tips: [
      "AI suggests optimal visit times",
      "Geographic clustering saves travel time",
      "Priority-based scheduling for urgent needs"
    ]
  },
  {
    id: "complete",
    title: "You're All Set!",
    subtitle: "Ready to make a difference",
    description: "You've completed the quick start guide. Remember, compassionate care starts with you. Welcome to the Goodwin Cares family!",
    icon: Heart,
    color: "bg-gradient-to-br from-rose-500 to-red-500",
    action: null,
    tips: [
      "Explore the dashboard for quick stats",
      "Check notifications regularly",
      "Reach out if you need help!"
    ]
  }
];

export default function QuickStartWizard({ isOpen, onClose, onComplete }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const step = WIZARD_STEPS[currentStep];
  const progress = ((currentStep) / (WIZARD_STEPS.length - 1)) * 100;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const Icon = step.icon;

  useEffect(() => {
    // Load completed steps from localStorage
    const saved = localStorage.getItem("quickstart_completed_steps");
    if (saved) {
      setCompletedSteps(JSON.parse(saved));
    }
  }, []);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
      return;
    }
    
    setIsAnimating(true);
    setTimeout(() => {
      // Mark current step as completed
      if (!completedSteps.includes(step.id)) {
        const newCompleted = [...completedSteps, step.id];
        setCompletedSteps(newCompleted);
        localStorage.setItem("quickstart_completed_steps", JSON.stringify(newCompleted));
      }
      setCurrentStep(prev => prev + 1);
      setIsAnimating(false);
    }, 200);
  };

  const handlePrev = () => {
    if (isFirstStep) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setIsAnimating(false);
    }, 200);
  };

  const handleAction = () => {
    if (step.action?.path) {
      // Mark step as completed before navigating
      if (!completedSteps.includes(step.id)) {
        const newCompleted = [...completedSteps, step.id];
        setCompletedSteps(newCompleted);
        localStorage.setItem("quickstart_completed_steps", JSON.stringify(newCompleted));
      }
      onClose();
      navigate(step.action.path);
    }
  };

  const handleComplete = () => {
    // Mark wizard as completed
    localStorage.setItem("quickstart_completed", "true");
    localStorage.setItem("quickstart_completed_date", new Date().toISOString());
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem("quickstart_skipped", "true");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="quickstart-wizard">
        {/* Progress bar */}
        <div className="h-1 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header with close */}
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {currentStep + 1} of {WIZARD_STEPS.length}
          </Badge>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-full"
            onClick={handleSkip}
            data-testid="quickstart-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Main content */}
        <div className={`transition-opacity duration-200 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          {/* Hero section */}
          <div className={`${step.color} text-white p-8 pb-12`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <p className="text-white/80 text-sm">{step.subtitle}</p>
                <h2 className="text-2xl font-bold">{step.title}</h2>
              </div>
            </div>
            <p className="text-white/90 max-w-lg">{step.description}</p>
          </div>

          {/* Tips section */}
          <div className="p-6 -mt-6">
            <Card className="shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="font-medium">Quick Tips</span>
                </div>
                <ul className="space-y-3">
                  {step.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">{tip}</span>
                    </li>
                  ))}
                </ul>

                {/* Action button for middle steps */}
                {step.action && (
                  <Button 
                    className="w-full mt-6" 
                    variant="outline"
                    onClick={handleAction}
                    data-testid={`quickstart-action-${step.id}`}
                  >
                    {step.action.label}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Step indicators */}
          <div className="px-6 pb-4">
            <div className="flex justify-center gap-2">
              {WIZARD_STEPS.map((s, index) => (
                <button
                  key={s.id}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep 
                      ? 'w-8 bg-primary' 
                      : completedSteps.includes(s.id)
                        ? 'bg-green-500'
                        : 'bg-muted'
                  }`}
                  onClick={() => setCurrentStep(index)}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="px-6 pb-6 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirstStep}
              className={isFirstStep ? 'invisible' : ''}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <div className="flex items-center gap-3">
              {!isLastStep && (
                <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                  Skip tour
                </Button>
              )}
              <Button onClick={handleNext} data-testid="quickstart-next">
                {isLastStep ? (
                  <>
                    Get Started
                    <Heart className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper hook to manage quick start state
export function useQuickStart() {
  const [showWizard, setShowWizard] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem("quickstart_completed") === "true";
    const skipped = localStorage.getItem("quickstart_skipped") === "true";
    setHasCompleted(completed);
    
    // Auto-show for new users who haven't completed or skipped
    if (!completed && !skipped) {
      // Small delay to let the page load first
      const timer = setTimeout(() => setShowWizard(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const openWizard = () => setShowWizard(true);
  const closeWizard = () => setShowWizard(false);
  const markComplete = () => {
    setHasCompleted(true);
    setShowWizard(false);
  };

  const resetWizard = () => {
    localStorage.removeItem("quickstart_completed");
    localStorage.removeItem("quickstart_skipped");
    localStorage.removeItem("quickstart_completed_steps");
    localStorage.removeItem("quickstart_completed_date");
    setHasCompleted(false);
  };

  return {
    showWizard,
    hasCompleted,
    openWizard,
    closeWizard,
    markComplete,
    resetWizard
  };
}
